import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import MiniPlayer from "./MiniPlayer";

// One audio element for the whole /investigations tree. The call page's
// transcript player and the docked mini player both drive this element, so
// playback survives tab changes and the hand-off between the two players is
// seamless (same element, same position — nothing restarts).

export type NowPlaying = { investigationId: string; callId: string };

type Ctx = {
  audio: HTMLAudioElement;
  nowPlaying: NowPlaying | null;
  playing: boolean;
  currentMs: number;
  // Play a moment of a call from anywhere (Leads, Timeline). The mini player
  // fetches the call and attaches the source.
  play: (investigationId: string, callId: string, startMs: number) => void;
  // Point the element at a call's audio. Idempotent per call; seeks/plays if asked.
  attachSource: (investigationId: string, callId: string, audioUrl: string, opts?: { startMs?: number; autoplay?: boolean }) => void;
  pendingStart: { callId: string; startMs: number } | null;
  close: () => void;
  // Horizontal extent of the main pane, for the docked player.
  bounds: { left: number; width: number } | null;
  // Height the docked player currently covers at the bottom of the main pane
  // (0 when hidden). Content that ends at the bottom edge pads by this much.
  dockHeight: number;
  setDockHeight: (h: number) => void;
};

const NowPlayingContext = createContext<Ctx | null>(null);

export function useNowPlaying() {
  const ctx = useContext(NowPlayingContext);
  if (!ctx) throw new Error("useNowPlaying must be used inside NowPlayingProvider");
  return ctx;
}

export const NowPlayingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  if (!audioRef.current) {
    const a = new Audio();
    a.preload = "auto";
    audioRef.current = a;
  }
  const audio = audioRef.current;
  const srcCallId = useRef<string | null>(null);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  const nowPlayingRef = useRef<NowPlaying | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const [pendingStart, setPendingStart] = useState<{ callId: string; startMs: number } | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState<{ left: number; width: number } | null>(null);
  const [dockHeight, setDockHeight] = useState(0);

  useEffect(() => {
    // Lives in the DOM (hidden) so it's inspectable and treated like any
    // other media element by the browser.
    audio.hidden = true;
    audio.dataset.role = "investigation-player";
    document.body.appendChild(audio);
    const onTime = () => setCurrentMs(Math.round(audio.currentTime * 1000));
    const onPlay = () => {
      setPlaying(true);
      // Anything that starts playing becomes "now playing" so leaving the
      // page keeps it going in the dock.
      if (!nowPlayingRef.current && srcCallId.current) {
        // investigationId is stashed on the element by attachSource
        const inv = audio.dataset.investigationId;
        if (inv) {
          nowPlayingRef.current = { investigationId: inv, callId: srcCallId.current };
          setNowPlaying(nowPlayingRef.current);
        }
      }
    };
    const onPause = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("seeked", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onPause);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("seeked", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onPause);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audio.remove();
    };
  }, [audio]);

  useLayoutEffect(() => {
    const measure = () => {
      const el = sentinelRef.current;
      if (!el) return;
      // Zero-size flex item: its left edge is the main pane's left edge, and
      // the pane runs to the viewport's right edge.
      const r = el.getBoundingClientRect();
      setBounds({ left: r.left, width: window.innerWidth - r.left });
    };
    measure();
    window.addEventListener("resize", measure);
    const t = setInterval(measure, 1000); // sidebar/drawer changes without a resize event
    return () => {
      window.removeEventListener("resize", measure);
      clearInterval(t);
    };
  }, []);

  const attachSource = useCallback<Ctx["attachSource"]>(
    (investigationId, callId, audioUrl, opts = {}) => {
      const { startMs, autoplay } = opts;
      const go = () => {
        if (startMs != null) audio.currentTime = startMs / 1000;
        if (autoplay) audio.play().catch(() => {});
      };
      audio.dataset.investigationId = investigationId;
      if (srcCallId.current !== callId) {
        srcCallId.current = callId;
        audio.src = audioUrl;
        audio.load();
        setCurrentMs(startMs || 0);
        const once = () => {
          audio.removeEventListener("loadedmetadata", once);
          audio.removeEventListener("canplay", once);
          go();
        };
        audio.addEventListener("loadedmetadata", once);
        audio.addEventListener("canplay", once);
      } else if (startMs != null || autoplay) {
        if (audio.readyState >= 1) go();
        else audio.addEventListener("loadedmetadata", go, { once: true });
      }
      // "Now playing" always tracks what the element holds: swapping the
      // source (opening another call's page) retires the previous call, so
      // the dock never advertises a call that's no longer loaded.
      if (autoplay || (nowPlayingRef.current && nowPlayingRef.current.callId !== callId)) {
        nowPlayingRef.current = { investigationId, callId };
        setNowPlaying(nowPlayingRef.current);
      }
      if (pendingStart?.callId === callId) setPendingStart(null);
    },
    [audio, pendingStart]
  );

  const play = useCallback<Ctx["play"]>(
    (investigationId, callId, startMs) => {
      nowPlayingRef.current = { investigationId, callId };
      setNowPlaying(nowPlayingRef.current);
      if (srcCallId.current === callId) {
        audio.currentTime = startMs / 1000;
        audio.play().catch(() => {});
      } else {
        // MiniPlayer resolves the audio URL and calls attachSource.
        setPendingStart({ callId, startMs });
      }
    },
    [audio]
  );

  const close = useCallback(() => {
    audio.pause();
    nowPlayingRef.current = null;
    setNowPlaying(null);
  }, [audio]);

  const value = useMemo<Ctx>(
    () => ({ audio, nowPlaying, playing, currentMs, play, attachSource, pendingStart, close, bounds, dockHeight, setDockHeight }),
    [audio, nowPlaying, playing, currentMs, play, attachSource, pendingStart, close, bounds, dockHeight]
  );

  return (
    <NowPlayingContext.Provider value={value}>
      {/* Zero-height block that reports the main pane's horizontal extent. */}
      <div ref={sentinelRef} className="w-0 h-0 shrink-0 basis-0" aria-hidden />
      {children}
      {nowPlaying && <MiniPlayer />}
    </NowPlayingContext.Provider>
  );
};
